import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { DocumentTypeDeveloper, LightTrackingOptions } from '../models/document-type';
import { FileNamingType } from '../models/filenamingtype';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule, DxDropDownBoxModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, DxiButtonModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-file-naming-types',
    templateUrl: './developer-file-naming-types.component.html',
    styleUrls: ['./developer-file-naming-types.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, DxDropDownBoxModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule]
})
export class DeveloperFileNamingTypesComponent implements OnInit, AfterViewInit {
    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    allowShowLog = false;
    fileNamingTypes: FileNamingType[] = [];
    distros: DocumentTypeDeveloper[] = [];
    dropDownOptions = { width: 500 };
    genericModalService: any;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_FilenamingType_Create');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_FilenamingType_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_FilenamingType_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.fileNamingTypes = [];

            let fileNamingTypes: FileNamingType[] = [];
            await Promise.all([
                this.developerService.getDocumentTypes().then(result => { this.distros = result.sort((a, b) => a.Name.localeCompare(b.Name)); }),
                this.developerService.getFileNamingTypes().then(result => { fileNamingTypes = result; }),
            ]);

            this.distros.splice(0, 0, {
                Id: -1,
                Name: '(Any)',
                UsableForDistributions: false,
                Description: '-',
                DocumentTypeGroupId: 0,
                CdmEmbeddedFileDataXPath: '',
                CdmEmbeddedFileNameXPath: '',
                CdmStylesheetArtifactId: null,
                FilenamingTypes: [],
                LightTrackingOptions: new LightTrackingOptions(),
                CreatedBy: null,
                CreatedDate: null,
                UpdatedBy: null,
                UpdatedDate: null
            });

            fileNamingTypes.forEach(e => { e.DistributionObjectId = e.DistributionObjectId || -1; });

            // Ensure that these are set last
            this.fileNamingTypes = fileNamingTypes;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        e.data = {
            FilenamingTypesId: 0,
            DistributionObjectId: -1,
            FilenamingTypeName: '',
            FilenamingKey: '',
            Example: '',
            SortOrder: 1
        } as FileNamingType;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as FileNamingType, item => this.saveFileNamingType(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as FileNamingType, item => this.saveFileNamingType(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as FileNamingType, item => this.removeFileNamingType(item));
    };

    async onRowInserted(e: any): Promise<void> {
        const fnt = e.data as FileNamingType;
        fnt.FilenamingTypesId = (await this.saveFileNamingType(fnt)).FilenamingTypesId;
    }

    async onRowUpdated(e: any): Promise<void> {
        const fnt = e.data as FileNamingType;
        await this.saveFileNamingType(fnt);
    }

    async removeFileNamingType(fileNamingType: FileNamingType): Promise<FileNamingType> {
        await this.developerService.deleteFileNamingType(fileNamingType.FilenamingTypesId);
        this.toastService.showToast('Filenaming type deleted...', ToastType.success);
        return fileNamingType;
    }

    async saveFileNamingType(fileNamingType: FileNamingType): Promise<FileNamingType> {
        fileNamingType.DistributionObjectId = (fileNamingType.DistributionObjectId == null || fileNamingType.DistributionObjectId < 0) ? null : fileNamingType.DistributionObjectId;
        const newFnt = await this.developerService.saveFileNamingType(fileNamingType);
        this.toastService.showToast('File naming type saved...', ToastType.success);
        newFnt.DistributionObjectId = newFnt.DistributionObjectId || -1;
        return newFnt;
    }

    // For use with grid in drop down template
    onSelectionChanged(selectedRowKeys, cellInfo, dropDownBoxComponent): void {
        cellInfo.setValue(selectedRowKeys[0]);
        if (selectedRowKeys.length > 0) {
            dropDownBoxComponent.close();
        }
    }
}
